import mermaid from 'mermaid';

// Initialize mermaid with default configuration
mermaid.initialize({
  startOnLoad: false,
  theme: 'neutral',
  securityLevel: 'strict',
  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  fontSize: 14,
  flowchart: {
    htmlLabels: true,
    curve: 'basis',
    useMaxWidth: false,
    padding: 20,
  },
  sequence: {
    diagramMarginX: 50,
    diagramMarginY: 10,
    actorMargin: 50,
    width: 150,
    height: 65,
    boxMargin: 10,
    boxTextMargin: 5,
    noteMargin: 10,
    messageMargin: 35,
    mirrorActors: true,
    bottomMarginAdj: 1,
    useMaxWidth: false,
    rightAngles: false,
    showSequenceNumbers: false,
  },
  gantt: {
    titleTopMargin: 25,
    barHeight: 20,
    barGap: 4,
    topPadding: 50,
    leftPadding: 75,
    gridLineStartPadding: 35,
    fontSize: 11,
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    numberSectionStyles: 4,
    axisFormat: '%Y-%m-%d',
    useMaxWidth: false,
  },
  class: {
    useMaxWidth: false,
  },
  state: {
    dividerMargin: 10,
    sizeUnit: 5,
    padding: 8,
    useMaxWidth: false,
  },
  er: {
    useMaxWidth: false,
  },
  pie: {
    useMaxWidth: false,
    textPosition: 0.5,
  },
  mindmap: {
    useMaxWidth: false,
    padding: 15,
  },
  quadrantChart: {
    useMaxWidth: false,
    chartWidth: 500,
    chartHeight: 500,
  },
  kanban: {
    useMaxWidth: false,
    padding: 15,
  },
  themeVariables: {
    // Base colors
    primaryColor: '#0056b3',
    primaryTextColor: '#ffffff',
    primaryBorderColor: '#0056b3',
    secondaryColor: '#f8f9fa',
    secondaryTextColor: '#212529',
    secondaryBorderColor: '#dee2e6',
    tertiaryColor: '#e9ecef',
    tertiaryTextColor: '#495057',
    tertiaryBorderColor: '#ced4da',
    
    // Flowchart
    nodeBorder: '#0056b3',
    nodeTextColor: '#212529',
    
    // Sequence diagram
    actorBorder: '#0056b3',
    actorTextColor: '#212529',
    signalColor: '#0056b3',
    
    // Class diagram
    classText: '#212529',
    
    // State diagram
    stateBkg: '#f8f9fa',
    stateBorder: '#0056b3',
    stateTextColor: '#212529',
    
    // Entity Relationship
    entityBorder: '#0056b3',
    entityTextColor: '#212529',
    
    // Note
    noteBkgColor: '#fff8dc',
    noteTextColor: '#212529',
    noteBorderColor: '#e6d285',
  }
});

/**
 * Render a Mermaid diagram
 * @param {string} code - Mermaid diagram code
 * @param {string} elementId - ID of the element to render the diagram in
 * @param {Object} options - Rendering options
 * @returns {Promise<string>} SVG string of the rendered diagram
 */
export const renderDiagram = async (code, elementId, options = {}) => {
  try {
    // Normalize the code to ensure proper rendering
    // This helps with case sensitivity issues in diagram types
    const normalizedCode = normalizeCode(code);
    
    // Update configuration if options are provided
    if (options.theme) {
      mermaid.initialize({ 
        theme: options.theme,
        flowchart: { useMaxWidth: false },
        sequence: { useMaxWidth: false },
        gantt: { useMaxWidth: false },
        pie: { useMaxWidth: false },
        er: { useMaxWidth: false },
        class: { useMaxWidth: false },
        state: { useMaxWidth: false },
        mindmap: { useMaxWidth: false },
        quadrantChart: { useMaxWidth: false },
        kanban: { useMaxWidth: false },
      });
    }

    // Render the diagram
    const { svg } = await mermaid.render(`mermaid-${Date.now()}`, normalizedCode);
    return svg;
  } catch (error) {
    console.error('Error rendering diagram:', error);
    throw error;
  }
};

/**
 * Normalize Mermaid code to ensure proper rendering
 * @param {string} code - Mermaid diagram code
 * @returns {string} Normalized Mermaid code
 */
function normalizeCode(code) {
  if (!code) return code;
  
  // Define mappings for diagram types that might have case variations
  const diagramTypes = {
    'quadrantchart': 'quadrantChart',
    'mindmap': 'mindmap',
    'flowchart': 'flowchart',
    'sequencediagram': 'sequenceDiagram',
    'classdiagram': 'classDiagram',
    'erdiagram': 'erDiagram',
    'statediagram': 'stateDiagram',
    'kanban': 'kanban'
  };
  
  // Check if the code starts with any of the diagram types (case insensitive)
  for (const [key, value] of Object.entries(diagramTypes)) {
    const regex = new RegExp(`^\\s*(${key})\\b`, 'i');
    if (regex.test(code)) {
      // Replace the diagram type with the correct case
      return code.replace(regex, value);
    }
  }
  
  return code;
}

/**
 * Parse a Mermaid diagram to check for syntax errors
 * @param {string} code - Mermaid diagram code
 * @returns {Promise<boolean>} True if valid, throws an error if invalid
 */
export const parseDiagram = async (code) => {
  try {
    // Normalize the code to ensure proper parsing
    const normalizedCode = normalizeCode(code);
    await mermaid.parse(normalizedCode);
    return true;
  } catch (error) {
    console.error('Error parsing diagram:', error);
    throw error;
  }
};

/**
 * Export a Mermaid diagram as SVG
 * @param {string} code - Mermaid diagram code
 * @param {Object} options - Export options
 * @returns {Promise<string>} SVG string of the diagram
 */
export const exportSVG = async (code, options = {}) => {
  try {
    const svg = await renderDiagram(code, null, options);
    return svg;
  } catch (error) {
    console.error('Error exporting diagram as SVG:', error);
    throw error;
  }
};

/**
 * Export a Mermaid diagram as PNG
 * @param {string} code - Mermaid diagram code
 * @param {Object} options - Export options
 * @returns {Promise<Blob>} PNG blob of the diagram
 */
export const exportPNG = async (code, options = {}) => {
  try {
    // First, get the SVG string
    const svgString = await exportSVG(code, options);
    
    // Create a temporary container to measure the SVG dimensions
    const tempContainer = document.createElement('div');
    tempContainer.style.position = 'absolute';
    tempContainer.style.visibility = 'hidden';
    tempContainer.style.pointerEvents = 'none';
    tempContainer.innerHTML = svgString;
    document.body.appendChild(tempContainer);
    
    // Get the SVG element
    const svgElement = tempContainer.querySelector('svg');
    
    // Get dimensions
    const width = svgElement.width.baseVal.value || 800;
    const height = svgElement.height.baseVal.value || 600;
    
    // Create a canvas element
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    
    // Set background color
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    
    // Convert SVG to data URL with base64 encoding
    const svgData = new XMLSerializer().serializeToString(svgElement);
    const svgBase64 = btoa(unescape(encodeURIComponent(svgData)));
    const dataUrl = `data:image/svg+xml;base64,${svgBase64}`;
    
    // Clean up the temporary container
    document.body.removeChild(tempContainer);
    
    // Return a promise that resolves with the PNG blob
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        try {
          // Draw the image on the canvas
          ctx.drawImage(img, 0, 0);
          
          // Convert canvas to PNG blob
          canvas.toBlob((blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to create PNG blob'));
            }
          }, 'image/png');
        } catch (error) {
          console.error('Error converting SVG to PNG:', error);
          reject(error);
        }
      };
      
      img.onerror = (error) => {
        console.error('Error loading SVG image:', error);
        reject(error);
      };
      
      // Set the image source to the data URL
      img.src = dataUrl;
    });
  } catch (error) {
    console.error('Error exporting diagram as PNG:', error);
    throw error;
  }
};

/**
 * Update Mermaid configuration
 * @param {Object} config - Mermaid configuration
 */
export const updateConfig = (config) => {
  try {
    // Ensure useMaxWidth is false for all diagram types
    const updatedConfig = {
      ...config,
      flowchart: { ...(config.flowchart || {}), useMaxWidth: false },
      sequence: { ...(config.sequence || {}), useMaxWidth: false },
      gantt: { ...(config.gantt || {}), useMaxWidth: false },
      pie: { ...(config.pie || {}), useMaxWidth: false },
      er: { ...(config.er || {}), useMaxWidth: false },
      class: { ...(config.class || {}), useMaxWidth: false },
      state: { ...(config.state || {}), useMaxWidth: false },
      mindmap: { ...(config.mindmap || {}), useMaxWidth: false },
      quadrantChart: { ...(config.quadrantChart || {}), useMaxWidth: false },
      kanban: { ...(config.kanban || {}), useMaxWidth: false },
    };
    
    mermaid.initialize(updatedConfig);
  } catch (error) {
    console.error('Error updating Mermaid configuration:', error);
    throw error;
  }
}; 